import React from 'react';
import { reportError } from '../lib/errorReporter';
import {
  clearChunkGuard,
  isChunkLoadFailure,
  reloadForChunk,
  shouldReloadForChunk,
} from '../lib/chunkRecovery';
import { AlertTriangleIcon, RefreshIcon } from './ui/Icons';
import styles from './ErrorBoundary.module.css';

/**
 * RED DE SEGURIDAD PARA ERRORES DE RENDER.
 *
 * ## 🔴 Por qué existe: no había NINGUNA
 *
 * Hasta ahora el dashboard no tenía un solo `componentDidCatch`. La consecuencia es la que se vio: un error en
 * cualquier componente **tumba el árbol entero**. En desarrollo se ve el recuadro rojo del servidor de CRA; en
 * producción no hay recuadro — hay **pantalla en blanco**, sin sidebar, sin forma de navegar y sin nada que
 * explique qué pasó. El usuario lo dijo exacto: *"ya estando en producción jamás debería marcar un error crítico
 * de esta manera"*.
 *
 * 🔑 Un `ErrorBoundary` no evita el error: evita que **un** componente roto se lleve la aplicación.
 *
 * ## 🔑 El caso que lo disparó, y por qué se trata aparte
 *
 * `this.errorCb is not a function`, desde el chunk de CSS de Mapbox. Es un fallo del cargador de webpack, no del
 * código de la pantalla: ocurre cuando un chunk cargado de forma diferida **se queda a medias** —porque se navegó
 * fuera antes de que terminara, o porque el servidor ya no tiene ese archivo tras un despliegue nuevo—.
 *
 * ⚠️ **Y ese caso NO se arregla mostrando un mensaje de error**, porque no hay nada que el usuario pueda hacer con
 * él: su navegador tiene JavaScript viejo pidiendo archivos que ya no existen. La única salida real es **recargar**
 * para traer la versión nueva. Por eso se detecta y se recarga solo, una vez.
 *
 * 📌 Una sola vez, con candado en `sessionStorage`. Sin el candado, un error que reaparezca al montar produciría
 * un bucle de recargas — que es peor que la pantalla en blanco, porque ni deja leer el mensaje.
 */

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * Cambia cuando cambia la ruta.
   *
   * 🔑 Es lo que **desatasca** al usuario. Sin esto, el estado de error se queda pegado: el sidebar sigue ahí,
   * pulsa otro módulo, la ruta cambia… y sigue viendo el mismo error, porque el boundary no se entera de que el
   * subárbol ya es otro. Eso se reporta como "la aplicación se congeló".
   */
  resetKey?: string;
  /** Texto para ubicar dónde ocurrió, en el reporte. */
  label?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(prev: ErrorBoundaryProps): void {
    /** Al navegar se limpia el error: el subárbol que falló ya no está montado. */
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    /**
     * ## Un chunk a medias se recarga; cualquier otra cosa se reporta
     *
     * 🔑 La distinción importa porque las dos respuestas correctas son opuestas. Un defecto del código hay que
     * **verlo** —si se recargara, se escondería y nadie lo arreglaría—. Un chunk que ya no existe en el servidor
     * no se puede arreglar desde el navegador: solo recargando.
     */
    if (isChunkLoadFailure({ message: error.message, name: error.name })) {
      const reloading = shouldReloadForChunk();

      /**
       * 📌 Se reporta **antes** de recargar: la recarga descarta el proceso, así que un reporte posterior no
       * llegaría a mandarse. El reportero guarda en `localStorage` y vacía la cola en el arranque siguiente, así
       * que el aviso sobrevive al salto.
       */
      reportError({
        message: reloading
          ? `Chunk load failed, reloading once: ${error.message}`
          : `Chunk load failed again after reload: ${error.message}`,
        kind: 'error',
        source: this.props.label || 'ErrorBoundary',
        stack: error.stack || null,
        context: {
          componentStack: info.componentStack?.slice(0, 1500) || null,
          where: this.props.label || 'ErrorBoundary',
          reloaded: reloading,
        },
      });

      if (reloading) {
        reloadForChunk();
        return;
      }

      /**
       * 🔴 Segundo fallo en la misma pestaña: ya no es una versión vieja, es algo roto de verdad. Se cae al
       * respaldo visible en lugar de recargar otra vez — un bucle de recargas es peor que un mensaje, porque ni
       * deja leerlo.
       */
      return;
    }

    reportError({
      message: error.message || 'Render error',
      kind: 'error',
      source: this.props.label || 'ErrorBoundary',
      stack: error.stack || null,
      context: {
        componentStack: info.componentStack?.slice(0, 1500) || null,
        label: this.props.label || null,
      },
    });
  }

  private retry = (): void => {
    /**
     * 📌 Reintentar **sin recargar**: en muchos casos el error venía de datos de una petición concreta, y volver a
     * montar el subárbol alcanza. Recargar la página entera costaría la sesión de scroll y los filtros abiertos.
     */
    clearChunkGuard();
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className={styles.host} role="alert">
        <span className={styles.icon}>
          <AlertTriangleIcon size={26} />
        </span>
        <h2 className={styles.title}>This section could not be displayed</h2>
        {/**
         * 🔑 Se dice **qué sigue funcionando**. Es la diferencia entre "algo se rompió" y "algo se rompió y puedo
         * seguir trabajando": sin esa frase, la reacción normal es cerrar la pestaña.
         */}
        <p className={styles.text}>
          The rest of the platform is still working — you can keep using the menu on the left. This has been
          reported automatically.
        </p>

        <div className={styles.actions}>
          <button type="button" className={styles.retryBtn} onClick={this.retry}>
            <RefreshIcon size={15} />
            Try again
          </button>
        </div>

        {/**
         * 📌 El detalle técnico va **colapsado** y al final. Quien reporta el problema necesita poder copiarlo; a
         * quien solo quiere seguir trabajando no le sirve de nada tenerlo encima.
         */}
        <details className={styles.details}>
          <summary className={styles.summary}>Technical detail</summary>
          <pre className={styles.pre}>{error.message}</pre>
        </details>
      </div>
    );
  }
}

export default ErrorBoundary;
